import { Fragment, type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ExternalLink,
  History,
  Images,
  Landmark,
  MapPin,
  Maximize2,
  MessageCircle,
  Minimize2,
  Paperclip,
  QrCode,
  Send,
  Smile,
  AudioLines,
  X,
} from 'lucide-react';
import { normalizeBankLogoUrl, normalizeQrCodeSrc } from '@/features/checkout/checkout.api';
import {
  claimGuestConversation,
  sendChatMessage,
  streamChatMessage,
  type ChatMessageBlock,
  type ChatPayment,
  type ChatProduct,
  type ChatReply,
  type ChatRequestContext,
  type ChatHistoryMessage,
} from './chat.api';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { getSharedSessionId } from '@/lib/session-id';
import { dispatchCartRefresh } from '@/features/cart/cart-sync';
import DeliverySelectionMap from '@/components/delivery/DeliverySelectionMap';
import { useChatHistory } from './useChatHistory';
import ChatHistoryDrawer from './ChatHistoryDrawer';
import { extractInlineImageUrls, stripInlineImageUrls } from './chat-images';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  reply?: ChatReply;
  loading?: boolean;
  durationMs?: number;
}

const SUGGESTIONS = [
  'Хүүхдийн бараа санал болго',
  'Хямдралтай бараа үзүүл',
  'Сагсан дахь бараагаа шалгая',
  'Хүргэлт хэдэн хоног вэ?',
];

const CART_KEYWORDS = ['сагс', 'нэм', 'хас', 'checkout', 'cart', 'order', 'захиалга'];
const CARGO_MAP_SUGGESTION = 'Cargo байршлыг газрын зургаар харах';
const CHAT_MESSAGES_STORAGE_KEY = 'chat_messages';
const CHAT_CONVERSATION_ID_STORAGE_KEY = 'chat_conversation_id';
const CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY = 'chat_guest_conversation_id';
const CHAT_MAX_PERSISTED = 40;

function loadPersistedMessages(): Message[] {
  try {
    const raw = sessionStorage.getItem(CHAT_MESSAGES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Message[];
    return Array.isArray(parsed) ? parsed.filter((m) => m.id && m.role && !m.loading) : [];
  } catch {
    return [];
  }
}

function persistMessages(messages: Message[]) {
  try {
    const toStore = messages.filter((m) => !m.loading).slice(-CHAT_MAX_PERSISTED);
    sessionStorage.setItem(CHAT_MESSAGES_STORAGE_KEY, JSON.stringify(toStore));
  } catch { /* quota exceeded — ignore */ }
}

function loadPersistedString(key: string): string | null {
  try {
    const value = sessionStorage.getItem(key)?.trim();
    return value || null;
  } catch {
    return null;
  }
}

function persistString(key: string, value: string | null) {
  try {
    if (value?.trim()) {
      sessionStorage.setItem(key, value.trim());
    } else {
      sessionStorage.removeItem(key);
    }
  } catch { /* storage unavailable — ignore */ }
}

function createClientRequestId() {
  return globalThis.crypto?.randomUUID?.()
    ?? `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isExternalButtonUrl(url: string) {
  if (url.startsWith('/')) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  if (typeof window === 'undefined') return true;

  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function shouldRefreshCart(reply: ChatReply) {
  const text = reply.text.toLowerCase();
  return CART_KEYWORDS.some((keyword) => text.includes(keyword));
}

function isCargoMapSuggestion(text: string) {
  return text.trim().toLowerCase() === CARGO_MAP_SUGGESTION.toLowerCase();
}

function getProductGalleryImages(product: ChatProduct): string[] {
  const images = [product.imageUrl, ...(product.galleryImages ?? [])].filter(
    (value): value is string => Boolean(value?.trim())
  );
  return Array.from(new Set(images));
}

function buildChatRequestContext(messages: Message[]): ChatRequestContext | null {
  const latestProductReply = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant' && (message.reply?.products?.length ?? 0) > 0)?.reply;

  const latestAssistantReply = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant' && message.reply)?.reply;

  const checkoutState = inferCheckoutState(latestAssistantReply);

  const lastShownProducts = (latestProductReply?.products ?? [])
    .filter((product): product is ChatProduct & { slug: string } => Boolean(product.slug?.trim()))
    .map((product) => ({
      slug: product.slug,
      title: product.title,
      price: product.price,
    }));

  if (lastShownProducts.length === 0 && !checkoutState) return null;

  return {
    ...(lastShownProducts.length > 0 ? {
      lastShownProducts,
      selectedProductSlug: lastShownProducts.length === 1 ? lastShownProducts[0].slug : null,
    } : {}),
    ...(checkoutState ? { checkoutState } : {}),
  };
}

function inferCheckoutState(reply: ChatReply | undefined): ChatRequestContext['checkoutState'] {
  if (!reply) return null;

  const hasLoginButton = reply.buttons?.some((button) =>
    button.url?.startsWith('/login') || button.title.toLowerCase().includes('нэвтр')
  );
  if (hasLoginButton && /захиалга|qpay|qr/i.test(reply.text)) {
    return 'login_required_for_checkout';
  }

  const hasCheckoutButton = reply.buttons?.some((button) =>
    button.url === '/checkout' || button.title.toLowerCase().includes('хүргэлт')
  );
  if (hasCheckoutButton && /хүргэлт|checkout|хаяг/i.test(reply.text)) {
    return 'shipping_required_for_checkout';
  }

  return null;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  return text.split(/(\*\*.*?\*\*)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${part}-${index}`} className="font-semibold text-slate-900 dark:text-slate-100">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return <Fragment key={`${part}-${index}`}>{part}</Fragment>;
  });
}

function isMetaLine(text: string): boolean {
  return /^(нийт(?:\s|$)|дээрхээс(?:\s|$)|үлдэх мөнгө(?:\s|$)|нийт дүн(?:\s|$)|total(?:\s|$))/i.test(text.trim());
}

function buildFallbackBlocks(text: string): ChatMessageBlock[] {
  const lines = text
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const blocks: ChatMessageBlock[] = [];
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    blocks.push({ type: 'list', items: listBuffer });
    listBuffer = [];
  };

  for (const line of lines) {
    const bulletMatch = line.match(/^[-*•]\s+(.*)$/);
    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
      continue;
    }

    flushList();
    if (isMetaLine(line)) {
      blocks.push({ type: 'meta', text: line });
      continue;
    }

    blocks.push({ type: 'paragraph', text: line });
  }

  flushList();
  return blocks;
}

function cleanBlocksOfImages(blocks: ChatMessageBlock[], urls: string[]): ChatMessageBlock[] {
  if (urls.length === 0) return blocks;
  const cleaned: ChatMessageBlock[] = [];
  for (const block of blocks) {
    if (block.type === 'list') {
      const items = block.items
        .map((item) => stripInlineImageUrls(item, urls))
        .filter((item) => item.trim().length > 0);
      if (items.length > 0) cleaned.push({ ...block, items });
    } else {
      const text = stripInlineImageUrls(block.text, urls);
      if (text.trim().length > 0) cleaned.push({ ...block, text });
    }
  }
  return cleaned;
}

function ChatImageGallery({ urls }: { urls: string[] }) {
  return (
    <div className="grid grid-cols-3 gap-2 pt-1">
      {urls.map((url, index) => (
        <a
          key={`${url}-${index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700"
        >
          <img
            src={url}
            alt={`Зураг ${index + 1}`}
            loading="lazy"
            className="aspect-square h-full w-full object-cover"
          />
        </a>
      ))}
    </div>
  );
}

function AssistantMessageBody({ text, blocks }: { text: string; blocks?: ChatMessageBlock[] }) {
  const imageUrls = extractInlineImageUrls(text);
  const cleanText = imageUrls.length > 0 ? stripInlineImageUrls(text, imageUrls) : text;
  const sourceBlocks = blocks && blocks.length > 0 ? blocks : buildFallbackBlocks(cleanText);
  const normalizedBlocks = cleanBlocksOfImages(sourceBlocks, imageUrls);

  return (
    <div className="space-y-3">
      {normalizedBlocks.map((block, index) => {
        if (block.type === 'summary') {
          return (
            <p
              key={`summary-${index}`}
              className="text-[16px] font-medium leading-8 tracking-[-0.02em] text-slate-900 dark:text-slate-100"
            >
              {renderInlineMarkdown(block.text)}
            </p>
          );
        }

        if (block.type === 'list') {
          return (
            <ul key={`list-${index}`} className="space-y-2.5 pl-0">
              {block.items.map((item, itemIndex) => (
                <li
                  key={`item-${itemIndex}`}
                  className="flex gap-3 text-[15px] leading-8 text-slate-700 dark:text-slate-200"
                >
                  <span className="mt-[11px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                  <span className="min-w-0">{renderInlineMarkdown(item)}</span>
                </li>
              ))}
            </ul>
          );
        }

        if (block.type === 'meta') {
          return (
            <p key={`meta-${index}`} className="pt-2 text-sm font-medium text-slate-500 dark:text-slate-400">
              {renderInlineMarkdown(block.text)}
            </p>
          );
        }

        return (
          <p key={`paragraph-${index}`} className="text-[15px] leading-8 text-slate-800 dark:text-slate-200">
            {renderInlineMarkdown(block.text)}
          </p>
        );
      })}
      {imageUrls.length > 0 && <ChatImageGallery urls={imageUrls} />}
    </div>
  );
}

function ChatPaymentCard({
  payment,
  brokenBankLogos,
  onLogoError,
}: {
  payment: ChatPayment;
  brokenBankLogos: Record<string, boolean>;
  onLogoError: (key: string) => void;
}) {
  const qrSrc = normalizeQrCodeSrc(payment.qrCode);
  const amount = Number(payment.amount || 0).toLocaleString('mn-MN');
  const orderLabel = payment.orderId.slice(0, 8).toUpperCase();
  const expiresAt = payment.expiresAt ? new Date(payment.expiresAt) : null;
  const expiresText = expiresAt && !Number.isNaN(expiresAt.getTime())
    ? expiresAt.toLocaleString('mn-MN')
    : null;

  return (
    <div className="w-[min(100%,640px)] overflow-hidden rounded-[28px] border border-emerald-200/80 bg-[linear-gradient(145deg,rgba(236,253,245,0.94),rgba(255,255,255,0.96)_44%,rgba(240,253,250,0.9))] p-4 shadow-[0_22px_54px_rgba(15,118,110,0.14)] dark:border-emerald-400/20 dark:bg-[linear-gradient(145deg,rgba(6,78,59,0.42),rgba(15,23,42,0.96)_42%,rgba(17,24,39,0.92))]">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex-shrink-0">
          <div className="rounded-[24px] border border-white bg-white p-3 shadow-[0_14px_32px_rgba(15,23,42,0.12)]">
            {qrSrc ? (
              <img src={qrSrc} alt="QPay QR code" className="h-40 w-40 rounded-2xl object-contain" />
            ) : (
              <div className="flex h-40 w-40 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <QrCode className="h-12 w-12" />
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
            <CreditCard className="h-4 w-4" />
            QPay төлбөр
          </div>
          <div>
            <p className="text-2xl font-black tracking-[-0.04em] text-slate-950 dark:text-white">₮{amount}</p>
            <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">Захиалга #{orderLabel}</p>
          </div>

          {expiresText ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200">
              <span>{expiresText} хүртэл хүчинтэй</span>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {payment.qrCodeUrl ? (
              <a
                href={payment.qrCodeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-950 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700 dark:bg-white dark:text-slate-950 dark:hover:bg-emerald-100"
              >
                QPay-р нээх
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
            <a
              href={`/orders/${payment.orderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/85 px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            >
              Захиалга харах
            </a>
          </div>
        </div>
      </div>

      {payment.bankUrls.length > 0 ? (
        <div className="mt-4 border-t border-emerald-100 pt-3 dark:border-white/10">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            <Landmark className="h-3.5 w-3.5" />
            Банкны апп-аар шууд төлөх
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {payment.bankUrls.slice(0, 9).map((bank, index) => {
              const key = `${bank.name}-${index}`;
              const logoSrc = normalizeBankLogoUrl(bank.logo ?? '');
              const showLogo = Boolean(logoSrc) && !brokenBankLogos[key];
              return (
                <a
                  key={key}
                  href={bank.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-2xl border border-white/70 bg-white/78 p-2 text-xs font-bold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm dark:bg-slate-900">
                    {showLogo ? (
                      <img
                        src={logoSrc}
                        alt={bank.name}
                        className="h-full w-full object-contain"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={() => onLogoError(key)}
                      />
                    ) : (
                      <span className="text-sm text-emerald-700 dark:text-emerald-300">{bank.name.charAt(0).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="min-w-0 truncate">{bank.name}</span>
                </a>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AssistantAvatar({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={`relative flex items-center justify-center rounded-[22px] border border-emerald-200/55 bg-[radial-gradient(circle_at_30%_26%,rgba(236,253,245,0.88),rgba(167,243,208,0.9)_26%,rgba(52,211,153,0.96)_66%,rgba(4,120,87,1))] shadow-[0_16px_34px_rgba(16,185,129,0.22)] ${
        compact ? 'h-10 w-10' : 'h-11 w-11'
      }`}
    >
      <div className="absolute inset-[3px] rounded-[18px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.34),transparent_48%)]" />
      <div className="absolute inset-[1px] rounded-[21px] border border-white/20" />
      <Bot className={`${compact ? 'h-[18px] w-[18px]' : 'h-5 w-5'} relative z-10 text-white`} />
      <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400" />
    </div>
  );
}

function getChatDateLabel() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year} оны ${month} сарын ${day}`;
}

function EmptyState({ onPick, maximized = false }: { onPick: (value: string) => void; maximized?: boolean }) {
  if (maximized) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center px-4 text-center">
        <p className="mb-12 text-sm font-bold text-slate-400">{getChatDateLabel()}</p>
        <div className="mb-5 text-4xl leading-none">✨</div>
        <h2 className="text-2xl font-black tracking-normal text-slate-900">
          Сайн байна уу <span className="inline-block">👋</span>
        </h2>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-1">
      <div className="relative overflow-hidden rounded-[24px] border border-white/62 bg-[linear-gradient(135deg,rgba(255,255,255,0.64),rgba(255,255,255,0.18))] px-4 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.78),0_14px_28px_rgba(148,163,184,0.12)] backdrop-blur-[16px] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(20,28,42,0.72),rgba(15,23,42,0.52))]">
        <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-white/80" />
        <div className="flex items-start gap-3">
          <AssistantAvatar compact />
          <div className="min-w-0 space-y-2 text-left">
            <p className="text-base font-semibold leading-6 tracking-[-0.03em] text-slate-900 dark:text-slate-100">
              Сайн байна уу. Юу хайж байна?
            </p>
            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
              Бараа санал болгох, сагс шалгах, захиалга болон хүргэлтийн асуултад тусална.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-300">
          Шуурхай сонголт
        </p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => onPick(suggestion)}
              className="rounded-full border border-white/60 bg-white/55 px-3.5 py-2 text-left text-xs font-semibold text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_16px_rgba(203,213,225,0.08)] backdrop-blur-[10px] transition-colors hover:bg-white/80 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadPersistedMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedCargoMaps, setExpandedCargoMaps] = useState<Record<string, boolean>>({});
  const [selectedCargoBranches, setSelectedCargoBranches] = useState<Record<string, string | null>>({});
  const [brokenPaymentBankLogos, setBrokenPaymentBankLogos] = useState<Record<string, boolean>>({});
  const [galleryProduct, setGalleryProduct] = useState<ChatProduct | null>(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionId = useRef(getSharedSessionId());
  const { session } = useAuth();
  const { language } = useTheme();
  const history = useChatHistory();
  const conversationIdRef = useRef<string | null>(loadPersistedString(CHAT_CONVERSATION_ID_STORAGE_KEY));
  const guestConversationIdRef = useRef<string | null>(loadPersistedString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY));
  const claimInFlightRef = useRef<string | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const lastMessageCountRef = useRef(messages.length);
  const [visualViewportHeight, setVisualViewportHeight] = useState<number | null>(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const assistantLabel = language === 'mn' ? 'Таны хөтөч' : 'Your guide';
  const launcherTitle = language === 'mn' ? 'Надтай шоппинг хийгээрэй' : 'Shop with me';
  const openChatLabel = language === 'mn' ? 'AI chat нээх' : 'Open AI chat';

  function rememberConversation(reply: ChatReply) {
    if (!reply.conversationId) return;
    const changed = conversationIdRef.current !== reply.conversationId;
    conversationIdRef.current = reply.conversationId;
    persistString(CHAT_CONVERSATION_ID_STORAGE_KEY, reply.conversationId);
    if (history.isLoggedIn) {
      guestConversationIdRef.current = null;
      persistString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY, null);
    } else {
      guestConversationIdRef.current = reply.conversationId;
      persistString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY, reply.conversationId);
    }
    if (changed && history.isLoggedIn) {
      history.refreshList();
    }
  }

  function resetLocalConversation() {
    conversationIdRef.current = null;
    guestConversationIdRef.current = null;
    persistString(CHAT_CONVERSATION_ID_STORAGE_KEY, null);
    persistString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY, null);
  }

  function isMessagesNearBottom() {
    const scroller = messagesScrollRef.current;
    if (!scroller) return true;
    return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 120;
  }

  function scrollMessagesToBottom(behavior: ScrollBehavior = 'auto') {
    const scroller = messagesScrollRef.current;
    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior });
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior });
  }

  function handleMessagesScroll() {
    shouldStickToBottomRef.current = isMessagesNearBottom();
  }

  useEffect(() => {
    if (!open) return;
    shouldStickToBottomRef.current = true;
    const scrollFrame = window.requestAnimationFrame(() => scrollMessagesToBottom('auto'));
    const canAutoFocus = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const focusTimer = canAutoFocus
      ? window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0)
      : undefined;

    return () => {
      window.cancelAnimationFrame(scrollFrame);
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open || historyOpen) {
      lastMessageCountRef.current = messages.length;
      return;
    }

    const messageCountIncreased = messages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;
    if (messageCountIncreased) {
      shouldStickToBottomRef.current = true;
    }
    if (!shouldStickToBottomRef.current) return;

    const scrollFrame = window.requestAnimationFrame(() => scrollMessagesToBottom('auto'));
    return () => window.cancelAnimationFrame(scrollFrame);
  }, [historyOpen, messages, open]);

  useEffect(() => {
    if (!open) return;

    let scrollFrame: number | null = null;
    const updateViewportMetrics = () => {
      const viewport = window.visualViewport;
      const nextHeight = Math.round(viewport?.height ?? window.innerHeight);
      const nextKeyboardOffset = Math.max(
        0,
        Math.round(viewport ? window.innerHeight - viewport.height - viewport.offsetTop : 0),
      );
      setVisualViewportHeight((current) => (current === nextHeight ? current : nextHeight));
      setKeyboardOffset((current) => (current === nextKeyboardOffset ? current : nextKeyboardOffset));

      if (shouldStickToBottomRef.current) {
        if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => scrollMessagesToBottom('auto'));
      }
    };

    updateViewportMetrics();
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateViewportMetrics);
    viewport?.addEventListener('scroll', updateViewportMetrics);
    window.addEventListener('resize', updateViewportMetrics);
    window.addEventListener('orientationchange', updateViewportMetrics);

    return () => {
      viewport?.removeEventListener('resize', updateViewportMetrics);
      viewport?.removeEventListener('scroll', updateViewportMetrics);
      window.removeEventListener('resize', updateViewportMetrics);
      window.removeEventListener('orientationchange', updateViewportMetrics);
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
    };
  }, [open]);

  useEffect(() => {
    persistMessages(messages);
  }, [messages]);

  useEffect(() => {
    const accessToken = session?.access_token;
    const guestConversationId = guestConversationIdRef.current;
    if (!accessToken || !guestConversationId || claimInFlightRef.current === guestConversationId) return;

    claimInFlightRef.current = guestConversationId;
    claimGuestConversation(accessToken, guestConversationId, sessionId.current)
      .then(() => {
        if (guestConversationIdRef.current === guestConversationId) {
          guestConversationIdRef.current = null;
          persistString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY, null);
        }
        history.refreshList();
      })
      .catch(() => {
        claimInFlightRef.current = null;
      });
  }, [history.refreshList, session?.access_token]);

  async function handleSend(text?: string) {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    shouldStickToBottomRef.current = true;
    setInput('');
    const requestContext = buildChatRequestContext(messages);
    const clientRequestId = createClientRequestId();

    const userMsg: Message = { id: Date.now().toString(), role: 'user', text: msg };
    const assistantId = `a-${Date.now()}`;
    const loadingMsg: Message = { id: assistantId, role: 'assistant', text: '', loading: true };
    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setLoading(true);

    const t0 = performance.now();
    let streamedText = '';
    let usedStream = false;

    try {
      await streamChatMessage(msg, sessionId.current, session?.access_token, {
        onChunk: (chunk) => {
          usedStream = true;
          if (chunk.type === 'text-delta') {
            streamedText += chunk.textDelta;
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, text: streamedText, loading: false } : m))
            );
          } else if (chunk.type === 'final') {
            const durationMs = Math.round(performance.now() - t0);
            rememberConversation(chunk.reply);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, text: chunk.reply.text, reply: chunk.reply, loading: false, durationMs }
                  : m
              )
            );
            if (shouldRefreshCart(chunk.reply)) dispatchCartRefresh();
          } else if (chunk.type === 'error') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, text: 'Уучлаарай, алдаа гарлаа. Дахин оролдоно уу.', loading: false }
                  : m
              )
            );
          }
        },
      }, requestContext, clientRequestId, conversationIdRef.current);
    } catch {
      if (!usedStream) {
        try {
          const reply = await sendChatMessage(
            msg,
            sessionId.current,
            session?.access_token,
            requestContext,
            clientRequestId,
            conversationIdRef.current,
          );
          const durationMs = Math.round(performance.now() - t0);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, text: reply.text, reply, loading: false, durationMs } : m
            )
          );
          rememberConversation(reply);
          if (shouldRefreshCart(reply)) dispatchCartRefresh();
        } catch {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, text: 'Уучлаарай, алдаа гарлаа. Дахин оролдоно уу.', loading: false }
                : m
            )
          );
        }
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleCargoMap(messageId: string) {
    setExpandedCargoMaps((prev) => ({ ...prev, [messageId]: !prev[messageId] }));
  }

  function handleSuggestionClick(suggestion: string, msg: Message) {
    if (isCargoMapSuggestion(suggestion) && msg.reply?.cargoBranches?.length) {
      setExpandedCargoMaps((prev) => ({ ...prev, [msg.id]: true }));
      return;
    }
    handleSend(suggestion);
  }

  function openProductGallery(product: ChatProduct) {
    if (getProductGalleryImages(product).length === 0) return;
    setGalleryProduct(product);
    setGalleryIndex(0);
  }

  const galleryImages = galleryProduct ? getProductGalleryImages(galleryProduct) : [];
  const isEmpty = messages.length === 0;
  const chatPanelStyle = open
    ? ({
        '--chat-visual-height': visualViewportHeight ? `${visualViewportHeight}px` : '100dvh',
        '--chat-keyboard-offset': `${keyboardOffset}px`,
        '--chat-keyboard-offset-half': `${Math.round(keyboardOffset / 2)}px`,
      } as CSSProperties)
    : undefined;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 overflow-hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        >
          <div
            className={`absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.38),rgba(2,6,23,0.56))] backdrop-blur-[6px] backdrop-saturate-75 transition-opacity duration-500 ease-out ${
              maximized ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <div
            className={`absolute inset-0 bg-[radial-gradient(circle_at_62%_12%,rgba(39,54,184,0.58),transparent_34%),linear-gradient(120deg,#05083b_0%,#080a49_42%,#20268f_100%)] transition-opacity duration-500 ease-out ${
              maximized ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>
      )}

      <div
        className={
          open
            ? 'pointer-events-none fixed inset-0 z-50'
            : `fixed right-3 z-50 flex flex-col items-end sm:right-4 ${
                open ? 'bottom-3 gap-0 sm:bottom-4' : 'bottom-5 gap-3 sm:bottom-6'
              }`
        }
      >
      {open && (
        <div
          className={`pointer-events-auto absolute flex flex-col overflow-hidden rounded-[34px] border border-white/62 bg-[linear-gradient(135deg,rgba(255,255,255,0.24),rgba(248,250,252,0.08)_48%,rgba(241,245,249,0.16))] shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_28px_72px_rgba(15,23,42,0.28)] backdrop-blur-[14px] backdrop-saturate-115 transition-all duration-500 ease-out [will-change:right,bottom,width,height,transform] motion-reduce:transition-none dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(10,16,24,0.94),rgba(15,23,42,0.86)_48%,rgba(9,14,22,0.9))] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_28px_72px_rgba(2,6,23,0.6)] ${
            maximized
              ? 'bottom-[calc(50%+var(--chat-keyboard-offset-half))] right-1/2 h-[calc(var(--chat-visual-height)-28px)] w-[calc(100vw-28px)] translate-x-1/2 translate-y-1/2 rounded-[22px] border-white/18 bg-white shadow-[0_28px_90px_rgba(0,0,0,0.28)] backdrop-blur-none dark:bg-white sm:h-[min(78vh,760px)] sm:w-[min(calc(100vw-80px),1480px)]'
              : 'bottom-[calc(0.75rem+var(--chat-keyboard-offset)+env(safe-area-inset-bottom))] right-3 h-[min(calc(var(--chat-visual-height)-24px),744px)] w-[min(calc(100vw-24px),449px)] translate-x-0 translate-y-0 sm:bottom-[calc(1rem+var(--chat-keyboard-offset)+env(safe-area-inset-bottom))] sm:right-4'
          }`}
          style={chatPanelStyle}
        >
          {!maximized && <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-[1px] rounded-[33px] border border-white/45" />
            <div className="absolute left-[8%] top-0 h-24 w-[36%] bg-white/8 blur-2xl" />
            <div className="absolute inset-x-10 top-0 h-px bg-white/90" />
            <div className="absolute left-[-72px] top-[132px] h-36 w-36 rounded-full border border-white/30 bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.68),rgba(255,255,255,0.2)_48%,rgba(148,163,184,0.16)_68%,rgba(15,23,42,0.14))] blur-[1px]" />
            <div className="absolute right-[-56px] top-[168px] h-28 w-28 rounded-full border border-white/28 bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.64),rgba(255,255,255,0.18)_45%,rgba(148,163,184,0.14)_70%,rgba(15,23,42,0.12))] blur-[1px]" />
            <div className="absolute left-[-48px] bottom-[84px] h-24 w-24 rounded-full bg-slate-900/70 blur-2xl" />
            <div className="absolute right-[24px] top-[30px] h-16 w-16 rounded-full bg-slate-900/25 blur-2xl" />
            <div className="absolute bottom-[12%] right-[18%] h-20 w-40 rotate-[12deg] bg-white/6 blur-2xl" />
          </div>}

          <div
            className={
              maximized
                ? 'relative overflow-hidden bg-white px-4 py-3 text-slate-900 sm:px-5'
                : 'relative overflow-hidden border-b border-white/50 bg-[linear-gradient(180deg,rgba(255,255,255,0.30),rgba(255,255,255,0.07))] px-4 py-3 text-slate-900 backdrop-blur-[10px] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] dark:text-slate-100 sm:px-5'
            }
          >
            {!maximized && (
              <>
                <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/18 blur-xl" />
                <div className="pointer-events-none absolute left-12 top-0 h-16 w-48 bg-white/8 blur-xl" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-white/80" />
              </>
            )}

            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AssistantAvatar compact={maximized} />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-[15px] font-bold leading-none tracking-normal text-slate-900">
                      {maximized ? 'KG Goods туслах' : assistantLabel}
                    </p>
                    {!maximized && <span className="rounded-full border border-white/76 bg-white/24 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.24em] text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.76)] backdrop-blur-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                      Online
                    </span>}
                  </div>
                  <p className={`mt-1 text-[13px] leading-5 ${maximized ? 'sr-only' : 'text-slate-500 dark:text-slate-300'}`}>
                    Бараа хайх, захиалга шалгах
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                {maximized && (
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-slate-100"
                    aria-label="Шинэ чат"
                    onClick={() => {
                      setMessages([]);
                      resetLocalConversation();
                    }}
                  >
                    <span className="text-2xl leading-none">+</span>
                  </button>
                )}
                {history.isLoggedIn && (
                  <button
                    onClick={() => { setHistoryOpen((v) => !v); if (!historyOpen) history.refreshList(); }}
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                      maximized
                        ? `text-slate-700 hover:bg-slate-100 ${historyOpen ? 'ring-2 ring-blue-400' : ''}`
                        : `border border-white/76 bg-white/22 text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_16px_rgba(226,232,240,0.12)] backdrop-blur-sm hover:bg-white/36 hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-slate-100 ${historyOpen ? 'ring-2 ring-blue-400' : ''}`
                    }`}
                    aria-label="Чат түүх"
                  >
                    <History className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setMaximized((v) => !v)}
                  className={
                    maximized
                      ? 'flex h-10 w-10 items-center justify-center rounded-[10px] border-2 border-blue-300 bg-white text-slate-700 shadow-sm transition-colors hover:bg-blue-50'
                      : 'flex h-9 w-9 items-center justify-center rounded-full border border-white/76 bg-white/22 text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_16px_rgba(226,232,240,0.12)] backdrop-blur-sm transition-colors hover:bg-white/36 hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-slate-100'
                  }
                  aria-label={maximized ? 'Chat жижигсгэх' : 'Chat томруулах'}
                >
                  {maximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className={
                    maximized
                      ? 'flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-slate-100'
                      : 'flex h-9 w-9 items-center justify-center rounded-full border border-white/76 bg-white/22 text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_16px_rgba(226,232,240,0.12)] backdrop-blur-sm transition-colors hover:bg-white/36 hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-slate-100'
                  }
                  aria-label="Chat хаах"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

          </div>

          <div
            className={
              maximized
                ? 'flex-1 overflow-hidden bg-white'
                : 'flex-1 overflow-hidden bg-[linear-gradient(180deg,rgba(248,250,252,0.84),rgba(241,245,249,0.88))] dark:bg-[linear-gradient(180deg,rgba(10,15,24,0.96),rgba(12,18,28,0.98))]'
            }
          >
            {historyOpen ? (
              <ChatHistoryDrawer
                conversations={history.conversations}
                selectedId={history.selectedId}
                historyMessages={history.historyMessages}
                loadingList={history.loadingList}
                loadingMessages={history.loadingMessages}
                onSelect={history.selectConversation}
                onDelete={history.deleteConversation}
                onArchive={history.archiveConversation}
                onNewChat={() => {
                  history.startNewChat();
                  resetLocalConversation();
                  setMessages([]);
                  setHistoryOpen(false);
                }}
                onClose={() => setHistoryOpen(false)}
                onLoadConversation={(msgs: ChatHistoryMessage[]) => {
                  const loaded: Message[] = msgs
                    .filter((m) => m.role !== 'SYSTEM_EVENT')
                    .map((m) => ({
                      id: m.id,
                      role: m.role === 'USER' ? 'user' as const : 'assistant' as const,
                      text: m.textRedacted,
                    }));
                  setMessages(loaded);
                  conversationIdRef.current = history.selectedId;
                  persistString(CHAT_CONVERSATION_ID_STORAGE_KEY, history.selectedId);
                  guestConversationIdRef.current = null;
                  persistString(CHAT_GUEST_CONVERSATION_ID_STORAGE_KEY, null);
                  setHistoryOpen(false);
                }}
              />
            ) : (
            <div
              ref={messagesScrollRef}
              onScroll={handleMessagesScroll}
              className={maximized ? 'h-full overflow-y-auto px-4 py-2 sm:px-8' : 'h-full overflow-y-auto px-3 py-3 sm:px-5 sm:py-4'}
            >
              <div
                className={
                  maximized
                    ? `mx-auto min-h-full max-w-[920px] ${isEmpty ? 'flex items-center justify-center' : 'space-y-4 py-5'}`
                    : 'min-h-full rounded-[24px] border border-white/55 bg-[linear-gradient(180deg,rgba(255,255,255,0.66),rgba(248,250,252,0.9))] px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.74),0_18px_34px_rgba(148,163,184,0.10)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(11,18,32,0.95))] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_34px_rgba(2,6,23,0.28)] sm:rounded-[28px] sm:px-4 sm:py-4'
                }
              >
                {isEmpty && <EmptyState onPick={handleSend} maximized={maximized} />}

                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex gap-3 ${maximized ? 'max-w-[82%]' : 'max-w-[92%]'} ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      {msg.role === 'assistant' ? (
                        <div className="pt-1">
                          <AssistantAvatar compact />
                        </div>
                      ) : null}

                      <div className={`flex flex-col gap-2 ${maximized ? 'max-w-full' : 'max-w-[86%]'} ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        {msg.role === 'assistant' ? (
                          <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 drop-shadow-[0_1px_0_rgba(255,255,255,0.18)] dark:text-slate-300">
                            {assistantLabel}
                          </span>
                        ) : null}

                        <div
                          className={`rounded-[24px] px-4 py-3 text-sm ${
                            msg.role === 'user'
                              ? 'rounded-br-md border border-white/18 bg-[linear-gradient(135deg,rgba(35,41,70,0.96),rgba(71,85,105,0.88))] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_16px_34px_rgba(15,23,42,0.26)]'
                              : 'rounded-bl-md border border-slate-200/88 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.94))] text-slate-900 shadow-[0_8px_20px_rgba(148,163,184,0.08)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(20,28,42,0.98),rgba(17,24,39,0.96))] dark:text-slate-100 dark:shadow-[0_8px_20px_rgba(2,6,23,0.24)]'
                          }`}
                        >
                          {msg.loading ? (
                            <span className="block">
                              <span className="flex items-center gap-2 text-[11px] font-medium text-slate-700 dark:text-slate-200">
                                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(16,185,129,0.14)]" />
                                AI туслах хариулт бэлдэж байна
                              </span>
                              <span className="mt-2 flex items-center gap-1 py-0.5">
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-bounce [animation-delay:0ms]" />
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-bounce [animation-delay:150ms]" />
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-bounce [animation-delay:300ms]" />
                              </span>
                            </span>
                          ) : msg.role === 'assistant' ? (
                            <AssistantMessageBody text={msg.text} blocks={msg.reply?.blocks} />
                          ) : (
                            msg.text
                          )}
                        </div>

                        {msg.role === 'assistant' && !msg.loading && msg.durationMs != null && (
                          <span className="px-1 text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
                            {msg.durationMs < 1000 ? `${msg.durationMs}ms` : `${(msg.durationMs / 1000).toFixed(1)}s`}
                          </span>
                        )}

                        {msg.reply?.buttons && msg.reply.buttons.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {msg.reply.buttons.map((btn, i) => {
                              if (btn.url) {
                                const isExternal = isExternalButtonUrl(btn.url);
                                return (
                                  <a
                                    key={i}
                                    href={btn.url}
                                    target={isExternal ? '_blank' : undefined}
                                    rel={isExternal ? 'noopener noreferrer' : undefined}
                                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/96 px-3 py-2 text-xs font-semibold text-slate-700 shadow-[0_6px_14px_rgba(15,23,42,0.06)] transition-colors hover:bg-slate-900 hover:text-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/12 dark:hover:text-white"
                                  >
                                    {btn.title}
                                    {isExternal ? <ExternalLink className="h-3 w-3" /> : null}
                                  </a>
                                );
                              }

                              return (
                                <button
                                  key={i}
                                  onClick={() => handleSend(btn.payload ?? btn.title)}
                                  className="rounded-full border border-slate-200 bg-white/98 px-3 py-2 text-xs font-medium text-slate-800 shadow-[0_6px_14px_rgba(15,23,42,0.04)] transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
                                >
                                  {btn.title}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {msg.reply?.suggestions && msg.reply.suggestions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {msg.reply.suggestions.map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => handleSuggestionClick(suggestion, msg)}
                                disabled={loading}
                                className="rounded-full border border-slate-200 bg-white/96 px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow-[0_8px_16px_rgba(148,163,184,0.06)] transition-all hover:-translate-y-0.5 hover:bg-slate-50 disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}

                        {msg.reply?.cargoBranches && msg.reply.cargoBranches.length > 0 && (
                          <div className="w-[min(100%,720px)] space-y-2">
                            <button
                              type="button"
                              onClick={() => toggleCargoMap(msg.id)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/96 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-900 hover:text-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                            >
                              <MapPin className="h-3.5 w-3.5" />
                              {expandedCargoMaps[msg.id] ? 'Газрын зураг нуух' : 'Газрын зураг харах'}
                            </button>

                            {expandedCargoMaps[msg.id] ? (
                              <div className="overflow-hidden rounded-[22px] border border-slate-200/85 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(248,250,252,0.88))] p-2 shadow-[0_16px_34px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(20,28,42,0.96),rgba(17,24,39,0.94))]">
                                <DeliverySelectionMap
                                  branches={msg.reply.cargoBranches}
                                  selectedBranchId={selectedCargoBranches[msg.id] ?? null}
                                  compact={!maximized}
                                  onSelect={(branch) => {
                                    setSelectedCargoBranches((prev) => ({ ...prev, [msg.id]: branch.id }));
                                  }}
                                />

                                {selectedCargoBranches[msg.id] ? (
                                  <div className="mt-2 rounded-2xl border border-slate-200 bg-white/88 px-3 py-2 text-xs text-slate-900 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                                    {(() => {
                                      const branch = msg.reply?.cargoBranches?.find(
                                        (item) => item.id === selectedCargoBranches[msg.id]
                                      );
                                      if (!branch) return null;
                                      return (
                                        <div>
                                          <p className="font-semibold">{branch.name}</p>
                                          <p className="mt-0.5 leading-5 text-slate-700 dark:text-slate-300">
                                            {[branch.city, branch.district, branch.addressLine1].filter(Boolean).join(', ')}
                                          </p>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        )}

                        {msg.reply?.payment ? (
                          <ChatPaymentCard
                            payment={msg.reply.payment}
                            brokenBankLogos={brokenPaymentBankLogos}
                            onLogoError={(key) => setBrokenPaymentBankLogos((prev) => ({ ...prev, [key]: true }))}
                          />
                        ) : null}

                        {msg.reply?.products && msg.reply.products.length > 0 && (
                          <div className="-mx-1 w-[calc(100%+8px)]">
                            <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                              Санал болгож буй бараа
                            </div>
                            <div className="flex gap-2.5 overflow-x-auto px-1 pb-2 scrollbar-thin scrollbar-thumb-slate-300">
                              {msg.reply.products.map((product, i) => {
                                const productImages = getProductGalleryImages(product);
                                const hasGallery = productImages.length > 0;
                                return (
                                  <div
                                    key={`${product.slug ?? product.url}-${i}`}
                                    className={`flex-shrink-0 overflow-hidden rounded-[22px] border border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(248,250,252,0.94))] shadow-[0_12px_24px_rgba(15,23,42,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(20,28,42,0.98),rgba(17,24,39,0.94))] ${
                                      maximized ? 'w-[212px] rounded-[24px]' : 'w-[146px]'
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openProductGallery(product)}
                                      disabled={!hasGallery}
                                      className={`block w-full overflow-hidden text-left ${hasGallery ? 'cursor-zoom-in' : 'cursor-default'}`}
                                      aria-label={`${product.title} зураг үзэх`}
                                    >
                                      {product.imageUrl ? (
                                        <img
                                          src={product.imageUrl}
                                          alt={product.title}
                                          className={`w-full object-cover ${maximized ? 'h-[164px]' : 'h-[112px]'}`}
                                        />
                                      ) : (
                                          <div className={`flex w-full items-center justify-center bg-slate-100 dark:bg-slate-800 ${maximized ? 'h-[164px]' : 'h-[112px]'}`}>
                                          <MessageCircle className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                                        </div>
                                      )}
                                    </button>

                                    <div className={`space-y-2 ${maximized ? 'p-4' : 'p-3'}`}>
                                      <div className="space-y-1">
                                        <p
                                          className={`line-clamp-2 font-semibold text-slate-800 dark:text-slate-100 ${
                                            maximized ? 'text-[13px] leading-5' : 'text-[11px] leading-4'
                                          }`}
                                        >
                                          {product.title}
                                        </p>
                                        {product.subtitle ? (
                                          <p className={`font-bold text-slate-800 dark:text-slate-200 ${maximized ? 'text-[13px]' : 'text-[11px]'}`}>
                                            {product.subtitle}
                                          </p>
                                        ) : null}
                                      </div>

                                      <div className="flex items-center gap-1.5">
                                        {hasGallery ? (
                                          <button
                                            type="button"
                                            onClick={() => openProductGallery(product)}
                                            className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-700 transition-colors hover:bg-slate-900 hover:text-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                                          >
                                            <Images className="h-3 w-3" />
                                            Зураг
                                          </button>
                                        ) : null}
                                        <a
                                          href={product.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:text-slate-100"
                                          aria-label={`${product.title} дэлгэрэнгүй үзэх`}
                                        >
                                          <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {msg.reply.products.length > 2 && (
                              <p className="mt-1 text-center text-[10px] text-slate-400 dark:text-slate-500">← {msg.reply.products.length} бараа, гүйлгэнэ үү →</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div ref={bottomRef} />
              </div>
            </div>
            )}
          </div>

          {galleryProduct && galleryImages.length > 0 && (
            <div
              className="absolute inset-0 z-20 flex bg-slate-950/70 p-3 backdrop-blur-sm sm:p-5"
              onClick={() => setGalleryProduct(null)}
            >
              <div
                className="mx-auto flex h-full w-full max-w-[760px] flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(9,14,24,0.98),rgba(8,13,23,0.94))] dark:shadow-[0_28px_80px_rgba(2,6,23,0.62)]"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 border-b border-slate-200/80 px-4 py-3 dark:border-white/10">
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{galleryProduct.title}</p>
                    {galleryProduct.subtitle ? (
                      <p className="mt-1 text-xs font-bold text-slate-800 dark:text-slate-300">{galleryProduct.subtitle}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setGalleryProduct(null)}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-white/20 dark:hover:text-slate-100"
                    aria-label="Зураг хаах"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="relative flex min-h-0 flex-1 items-center justify-center bg-slate-100 dark:bg-slate-950">
                  <img
                    src={galleryImages[galleryIndex]}
                    alt={`${galleryProduct.title} зураг ${galleryIndex + 1}`}
                    className="max-h-full w-full object-contain"
                  />

                  {galleryImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setGalleryIndex((index) => (index === 0 ? galleryImages.length - 1 : index - 1))}
                        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/90 text-slate-700 shadow-lg transition-colors hover:bg-slate-900 hover:text-white dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-white/12 dark:hover:text-white"
                        aria-label="Өмнөх зураг"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setGalleryIndex((index) => (index + 1) % galleryImages.length)}
                        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/90 text-slate-700 shadow-lg transition-colors hover:bg-slate-900 hover:text-white dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-white/12 dark:hover:text-white"
                        aria-label="Дараах зураг"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>

                <div className="space-y-3 border-t border-slate-200/80 bg-white px-4 py-3 dark:border-white/10 dark:bg-[rgba(8,13,23,0.96)]">
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      {galleryIndex + 1} / {galleryImages.length}
                    </span>
                    <a
                      href={galleryProduct.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white"
                    >
                      Дэлгэрэнгүй
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>

                  {galleryImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {galleryImages.map((image, index) => (
                        <button
                          key={`${image}-${index}`}
                          type="button"
                          onClick={() => setGalleryIndex(index)}
                          className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-2xl border transition-colors ${
                            index === galleryIndex
                              ? 'border-slate-900 ring-2 ring-slate-300 dark:border-slate-100 dark:ring-slate-600'
                              : 'border-slate-200 opacity-70 hover:opacity-100 dark:border-white/10'
                          }`}
                          aria-label={`${index + 1}-р зураг сонгох`}
                        >
                          <img src={image} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div
            className={
              maximized
                ? 'bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2'
                : 'border-t border-white/55 bg-[linear-gradient(180deg,rgba(255,255,255,0.16),rgba(255,255,255,0.08))] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-[8px] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]'
            }
          >
            {!maximized && (
              <div className="mb-2 flex items-center justify-between px-1 text-[11px] text-muted-foreground">
                <span>{assistantLabel}</span>
                <span>{loading ? 'Хариулт бэлдэж байна...' : 'Enter дарж илгээнэ'}</span>
              </div>
            )}
            <div className={maximized ? 'mx-auto max-w-[700px]' : ''}>
              <div
                className={
                  maximized
                    ? 'flex items-center gap-2 rounded-full bg-[#f7f7f7] px-3 py-2'
                    : 'flex items-center gap-2 rounded-[22px] border border-white/64 bg-white/72 px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.78),0_10px_20px_rgba(203,213,225,0.08)] backdrop-blur-[10px] dark:border-white/10 dark:bg-slate-950/42'
                }
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder={maximized ? 'Энд асуултаа бичээрэй...' : 'Бараа хайх...'}
                  disabled={loading}
                  className={`min-w-0 flex-1 bg-transparent px-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-50 ${
                    maximized ? 'py-2.5' : 'py-2 dark:text-slate-100 dark:placeholder:text-slate-500'
                  }`}
                />
                {maximized && (
                  <>
                    <button
                      type="button"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-800"
                      aria-label="Файл хавсаргах"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-800"
                      aria-label="Дуу хоолой"
                    >
                      <AudioLines className="h-5 w-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleSend()}
                  disabled={loading || !input.trim()}
                  className={
                    maximized
                      ? 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_10px_20px_rgba(37,99,235,0.24)] transition-all hover:scale-[1.03] hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:opacity-70'
                      : 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[18px] bg-emerald-500 text-white shadow-[0_10px_20px_rgba(16,185,129,0.22)] transition-all hover:scale-[1.03] hover:bg-emerald-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:opacity-70 dark:disabled:bg-slate-700 dark:disabled:text-slate-400'
                  }
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              {maximized && (
                <div className="mt-2 flex items-center justify-center gap-2 text-[11px] font-semibold text-slate-500">
                  <span>Powered by</span>
                  <span className="font-bold text-slate-700">KG Goods</span>
                  <span className="h-3 w-px bg-slate-300" />
                  <span>AI assistant</span>
                  <span className="flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px]">i</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="group flex items-center gap-3 rounded-full border border-white/74 bg-[linear-gradient(135deg,rgba(255,255,255,0.7),rgba(244,244,245,0.2))] px-3 py-2 text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.86),0_12px_24px_rgba(148,163,184,0.14)] backdrop-blur-[10px] backdrop-saturate-120 transition-all hover:scale-[1.02] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_18px_32px_rgba(15,23,42,0.18)] active:scale-[0.99] dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(10,16,24,0.94),rgba(15,23,42,0.82)_52%,rgba(9,14,22,0.86))] dark:text-slate-100 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_18px_28px_rgba(2,6,23,0.4)]"
          aria-label={openChatLabel}
        >
          {open ? (
            <X className="h-6 w-6" />
          ) : (
            <>
              <div className="relative flex h-11 w-11 items-center justify-center rounded-full border border-emerald-200/55 bg-[radial-gradient(circle_at_30%_26%,rgba(236,253,245,0.9),rgba(167,243,208,0.92)_26%,rgba(52,211,153,0.98)_66%,rgba(4,120,87,1))] shadow-[0_12px_24px_rgba(16,185,129,0.22)]">
                <div className="absolute inset-[3px] rounded-full bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.3),transparent_48%)]" />
                <Smile className="relative z-10 h-5 w-5 text-white" />
              </div>
              <div className="hidden min-w-0 text-left sm:block">
                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-600/90 dark:text-emerald-300/80">
                  {assistantLabel}
                </div>
                <div className="text-sm font-semibold tracking-[-0.02em] text-slate-900 dark:text-slate-100">
                  {launcherTitle}
                </div>
              </div>
            </>
          )}
        </button>
      )}
      </div>
    </>
  );
}
